import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus, InteractionRequiredAuthError } from '@azure/msal-browser';
import api from '../services/api';
import { TOKEN_KEY, consumePostLoginRedirect } from '../services/authService';
import type { LoginResponse } from '../types';

// ── DO NOT import useAuth here ────────────────────────────────────────────────
// useAuth() calls refreshToken() on mount. At /auth/callback there is no
// session yet, so that call returns 401. The api.ts interceptor catches any
// /auth/refresh 401 and does window.location.href='/login', which kills the
// page before MSAL has time to process the auth code in the URL.

export default function AuthCallback() {
  const navigate                            = useNavigate();
  const { instance, accounts, inProgress }  = useMsal();
  const [status, setStatus]                 = useState('Processing login…');
  const [error,  setError]                  = useState('');
  const processed                           = useRef(false);

  useEffect(() => {
    console.log('[AuthCallback] inProgress:', inProgress, '| accounts:', accounts.length);

    // MsalProvider calls handleRedirectPromise() internally on every page load.
    // Wait for it to finish — inProgress drops to None and accounts[] is populated.
    if (inProgress !== InteractionStatus.None) return;
    if (processed.current) return;
    processed.current = true;

    const account = accounts[0];
    console.log('[AuthCallback] account:', account?.username ?? 'none');

    if (!account) {
      console.warn('[AuthCallback] No MSAL account after redirect — returning to login');
      navigate('/login', { replace: true });
      return;
    }

    setStatus('Verifying your account…');

    const silentRequest = {
      scopes:  ['openid', 'profile', 'email', 'User.Read'],
      account,
    };

    instance
      .acquireTokenSilent(silentRequest)
      .catch((silentErr: unknown) => {
        if (silentErr instanceof InteractionRequiredAuthError) {
          console.warn('[AuthCallback] Silent acquisition failed — falling back to redirect');
          return instance.acquireTokenRedirect(silentRequest).then(() => null);
        }
        throw silentErr;
      })
      .then((result) => {
        if (!result) return null; // acquireTokenRedirect navigates away

        // accessToken has Graph audience — backend uses it to call GET /graph/me
        // idToken has app audience  — backend decodes it (no verify) for amr/tid claims
        const accessToken = result.accessToken;
        const idToken     = result.idToken;
        const amr         = (result.idTokenClaims as any)?.amr ?? [];
        console.log('[AuthCallback] accessToken:', !!accessToken, '| idToken:', !!idToken, '| amr:', JSON.stringify(amr));

        if (!accessToken) {
          throw new Error(
            'Microsoft did not return an access token. ' +
            'Ensure the User.Read scope is granted in the Azure app registration.',
          );
        }

        setStatus('Signing in to portal…');
        return api.post<LoginResponse>('/auth/azure-sso', {
          accessToken,
          idToken: idToken || undefined,
        });
      })
      .then((response) => {
        if (!response) return;
        const { data } = response;
        console.log('[AuthCallback] Backend success | user:', data.user?.email, '| firstLogin:', data.isFirstLogin);

        // Persist to both in-memory global AND sessionStorage so that:
        // a) api.ts request interceptor can attach the Bearer header immediately
        // b) useAuth()'s mount effect on PrivateLayout finds a fresh token and
        //    skips POST /auth/refresh entirely (calls lightweight GET /auth/me instead)
        window.__AUTH_TOKEN__ = data.token;
        sessionStorage.setItem(TOKEN_KEY, data.token);
        navigate(consumePostLoginRedirect(), { replace: true, state: { isFirstLogin: data.isFirstLogin } });
      })
      .catch((err: any) => {
        const raw = err?.response?.data ?? err?.message ?? String(err);
        console.error('[AuthCallback] Auth failed:', raw);
        const msg: string =
          err?.response?.data?.message ??
          err?.message ??
          'Sign-in failed. Please try again.';
        setError(msg);
        setTimeout(() => navigate('/login', { replace: true }), 4000);
      });
  }, [inProgress]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{
      display:        'flex',
      flexDirection:  'column',
      justifyContent: 'center',
      alignItems:     'center',
      height:         '100vh',
      background:     '#010E23',
      color:          'white',
      fontFamily:     'system-ui, sans-serif',
    }}>
      {error ? (
        <>
          <div style={{ fontSize: 32, marginBottom: 16 }}>⚠️</div>
          <div style={{ fontSize: 15, color: '#f87171', maxWidth: 360, textAlign: 'center', marginBottom: 8, lineHeight: 1.5 }}>
            {error}
          </div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
            Redirecting back to login…
          </div>
        </>
      ) : (
        <>
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            border: '3px solid rgba(255,255,255,0.15)',
            borderTopColor: '#0078d4',
            animation: 'spin 0.8s linear infinite',
            marginBottom: 20,
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ fontSize: 16, marginBottom: 6 }}>{status}</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>Please wait…</div>
        </>
      )}
    </div>
  );
}
